import { AlertCircle, FileX, RefreshCw, Search, Settings2 } from 'lucide-react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useDeleteTransactionDocument,
  useDownloadTransactionDocument,
  usePreviewTransactionDocument,
  useTransactionDocuments,
  useUploadTransactionDocument,
} from '@/hooks/useTransactionDocuments';
import type { TransactionDocument } from '@/types/transaction-document';
import { DocumentListItem } from './document-list-item';
import { DocumentUploadZone } from './document-upload-zone';
import { DocumentsEmailModal } from './documents-email-modal';

interface TransactionDocumentsTabProps {
  transactionReference: string;
  externalId?: string;
  accountCode?: string;
  customerName?: string;
  headerReference?: string;
  transactionDate?: string;
  description?: string;
  transactionType?: string;
  customerId?: string;
  accountNumber?: string;
}

export function TransactionDocumentsTab({
  transactionReference,
  externalId,
  accountCode,
  customerName,
  headerReference,
  transactionDate,
  description,
  transactionType,
  customerId,
  accountNumber,
}: TransactionDocumentsTabProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [emailModalOpen, setEmailModalOpen] = useState(false);

  const selectedIdsSnapshotRef = useRef<{ id: string; fileName: string; fileType: string }[]>([]);

  // Data hooks
  const { data, isLoading, isError, refetch, isFetching } = useTransactionDocuments(transactionReference, externalId);

  const uploadMutation = useUploadTransactionDocument();
  const deleteMutation = useDeleteTransactionDocument();
  const downloadMutation = useDownloadTransactionDocument();
  const previewMutation = usePreviewTransactionDocument();

  const documents = data?.documents ?? [];

  // Client-side search filter
  const filteredDocuments = useMemo(() => {
    if (!searchQuery.trim()) return documents;
    const q = searchQuery.toLowerCase();
    return documents.filter((doc) => doc.fileName.toLowerCase().includes(q));
  }, [documents, searchQuery]);

  // Selection helpers
  const allVisibleSelected = filteredDocuments.length > 0 && filteredDocuments.every((d) => selectedIds.has(d.id));

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(new Set(filteredDocuments.map((d) => d.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleSelectChange = (id: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  // Upload
  const handleUpload = (file: File) => {
    uploadMutation.mutate(
      {
        transactionReference,
        file,
        externalId,
        accountCode,
        customerName,
        headerReference,
        transactionDate,
        description,
        transactionType,
      },
      {
        onSuccess: () => toast.success('Document uploaded successfully'),
        onError: () => toast.error('Failed to upload document'),
      },
    );
  };

  // Preview — click on filename opens preview in new tab
  const handlePreview = (document: TransactionDocument) => {
    previewMutation.mutate(
      { documentId: document.id },
      {
        onError: () => toast.error('Failed to preview document'),
      },
    );
  };

  // Download
  const handleDownload = (document: TransactionDocument) => {
    downloadMutation.mutate(
      { documentId: document.id, fileName: document.fileName },
      {
        onError: () => toast.error('Failed to download document'),
      },
    );
  };

  // Delete
  const handleDelete = (document: TransactionDocument) => {
    setDeletingId(document.id);
    deleteMutation.mutate(
      { documentId: document.id, transactionReference },
      {
        onSuccess: () => {
          toast.success('Document deleted');
          setSelectedIds((prev) => {
            const next = new Set(prev);
            next.delete(document.id);
            return next;
          });
        },
        onError: () => toast.error('Failed to delete document'),
        onSettled: () => setDeletingId(null),
      },
    );
  };

  // Refresh
  const handleRefresh = () => {
    setSearchQuery('');
    refetch();
  };

  const handleEmailClick = useCallback(() => {
    selectedIdsSnapshotRef.current = [...selectedIds].map((id) => {
      const doc = documents.find((d) => d.id === id);
      return {
        id,
        fileName: doc?.fileName ?? `document_${id}`,
        fileType: doc?.fileType ?? 'application/octet-stream',
      };
    });
    setEmailModalOpen(true);
  }, [selectedIds, documents]);

  const handleEmailSuccess = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const selectedCount = selectedIds.size;

  return (
    <div className="flex flex-col gap-3 px-4 py-3 h-full">
      {/* Upload zone */}
      <DocumentUploadZone onUpload={handleUpload} isUploading={uploadMutation.isPending} />

      {/* Search bar + refresh */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Start typing Document Name"
            className="pl-8 h-8 text-sm"
            aria-label="Search documents"
          />
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-muted-foreground hover:text-foreground flex-shrink-0"
          onClick={handleRefresh}
          disabled={isFetching}
          aria-label="Refresh documents"
          title="Refresh"
        >
          <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {/* Select all + count + settings */}
      {!isLoading && !isError && filteredDocuments.length > 0 && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Checkbox
              id="select-all"
              checked={allVisibleSelected}
              onCheckedChange={(v) => handleSelectAll(v === true)}
              aria-label="Select all documents"
            />
            <label htmlFor="select-all" className="text-xs font-medium cursor-pointer select-none">
              Select All
            </label>
            <span className="text-xs text-muted-foreground">
              — {selectedCount} of {filteredDocuments.length} selected
            </span>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-muted-foreground hover:text-foreground"
            aria-label="Column settings"
            title="Settings"
          >
            <Settings2 className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Document list */}
      <div className="flex-1 overflow-y-auto space-y-2 min-h-0">
        {isLoading ? (
          // Skeleton loading state
          <div className="space-y-2">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-start gap-3 rounded-lg border p-3">
                <Skeleton className="h-4 w-4 mt-1 rounded flex-shrink-0" />
                <Skeleton className="h-9 w-9 rounded-md flex-shrink-0" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-48" />
                  <Skeleton className="h-3 w-64" />
                  <Skeleton className="h-3 w-40" />
                </div>
              </div>
            ))}
          </div>
        ) : isError ? (
          // Error state
          <div className="flex flex-col items-center justify-center py-10 text-center gap-3">
            <AlertCircle className="h-10 w-10 text-destructive/60" />
            <div>
              <p className="text-sm font-medium">Failed to load documents</p>
              <p className="text-xs text-muted-foreground mt-1">
                There was a problem retrieving documents for this transaction.
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
              Try Again
            </Button>
          </div>
        ) : filteredDocuments.length === 0 ? (
          // Empty state
          <div className="flex flex-col items-center justify-center py-10 text-center gap-3">
            <FileX className="h-10 w-10 text-muted-foreground/50" />
            <div>
              <p className="text-sm font-medium text-muted-foreground">
                {searchQuery ? 'No documents match your search' : 'No documents found for this transaction'}
              </p>
              {searchQuery && (
                <button onClick={() => setSearchQuery('')} className="text-xs text-primary hover:underline mt-1">
                  Clear search
                </button>
              )}
            </div>
          </div>
        ) : (
          filteredDocuments.map((doc) => (
            <DocumentListItem
              key={doc.id}
              document={doc}
              isSelected={selectedIds.has(doc.id)}
              onSelectChange={handleSelectChange}
              onPreview={handlePreview}
              onDownload={handleDownload}
              onDelete={handleDelete}
              isDeleting={deletingId === doc.id}
            />
          ))
        )}
      </div>

      {/* Bulk action toolbar — shown when ≥1 selected */}
      {selectedCount > 0 && (
        <div className="flex items-center justify-between rounded-md border bg-muted/50 px-3 py-2 text-xs font-medium gap-2 flex-shrink-0">
          <span className="text-muted-foreground">
            {selectedCount} document{selectedCount > 1 ? 's' : ''} selected
          </span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="h-7 text-xs" onClick={handleEmailClick}>
              Email
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs"
              onClick={() => {
                for (const id of selectedIds) {
                  const doc = documents.find((d) => d.id === id);
                  if (doc) handleDownload(doc);
                }
              }}
            >
              Download
            </Button>
          </div>
        </div>
      )}

      <DocumentsEmailModal
        open={emailModalOpen}
        onOpenChange={setEmailModalOpen}
        customerId={customerId ?? ''}
        customerName={customerName ?? ''}
        accountNumber={accountNumber}
        documents={selectedIdsSnapshotRef.current}
        onSuccess={handleEmailSuccess}
      />
    </div>
  );
}
